import { PagedResponse } from 'types/Pagination';
import HTTPClient, { HTTPClientParameters } from './HTTPClient';
import cache from './cache';
import {
    DriverTrends,
    DriverReviewsSummary,
    InsightsPeriod,
    DriverReviewSample,
    DriverReviewReason,
    DriverReviewRegionalRanking,
    PeakRentalSummary,
    PeakRentalByHour,
    PeakRentalByDayOfWeek,
    DriverDestination,
    TransientDrivers,
    TenantUsage,
    TenantUsageMonthly,
    TenantInsightsPeriod,
    PropertyInsightsSummary,
} from 'types/PropertyInsights';
import { buildQueryParams } from 'utils/helpers';
import { ReviewsRating } from 'components/PropertyInsights/helpers';

export const SAMPLES_REVIEW_PAGE_SIZE = 10; // should be in sync with backend

type PropertyInsightsClientParams = HTTPClientParameters;

class PropertyInsightsClient extends HTTPClient {
    constructor(params: PropertyInsightsClientParams) {
        super(params);
        this.headers.set('Content-Type', 'application/json');
    }

    @cache(5 * 60000)
    getDriverTrends(spotUuid: string, startDate: string, endDate: string): Promise<DriverTrends> {
        return this.get(
            `spots/${spotUuid}/driver-trends${buildQueryParams({
                startDate,
                endDate,
            })}`
        );
    }

    @cache(5 * 60000)
    getDriverReviewsSummary(spotUuid: string, period: InsightsPeriod): Promise<DriverReviewsSummary> {
        return this.get(`spots/${spotUuid}/reviews/summary?period=${period}`);
    }

    @cache(5 * 60000)
    getDriverReviewSamples(
        spotUuid: string,
        period: InsightsPeriod,
        rating: ReviewsRating,
        page: number
    ): Promise<PagedResponse<DriverReviewSample>> {
        return this.get(
            `spots/${spotUuid}/reviews/samples${buildQueryParams({
                period,
                rating,
                page,
            })}`
        );
    }

    @cache(5 * 60000)
    getDriverReviewReasons(spotUuid: string, period: InsightsPeriod): Promise<DriverReviewReason[]> {
        return this.get(`spots/${spotUuid}/reviews/reasons?period=${period}&limit=6`);
    }

    @cache(5 * 60000)
    getDriverReviewRegionalRanking(spotUuid: string, period: InsightsPeriod): Promise<DriverReviewRegionalRanking> {
        return this.get(`spots/${spotUuid}/reviews/regional-ranking?period=${period}`);
    }

    @cache(5 * 60000)
    getPeakRentalsSummary(spotUuid: string, period: InsightsPeriod): Promise<PeakRentalSummary> {
        return this.get(`spots/${spotUuid}/peak-rentals/summary?period=${period}`);
    }

    @cache(5 * 60000)
    getPeakRentalsByHour(spotUuid: string, period: InsightsPeriod): Promise<PeakRentalByHour[]> {
        return this.get(`spots/${spotUuid}/peak-rentals/hourly?period=${period}`);
    }

    @cache(5 * 60000)
    getPeakRentalsByDayOfWeek(spotUuid: string, period: InsightsPeriod): Promise<PeakRentalByDayOfWeek[]> {
        return this.get(`spots/${spotUuid}/peak-rentals/day-of-week?period=${period}`);
    }

    @cache(5 * 60000)
    getDriverDestinations(spotUuid: string): Promise<DriverDestination[]> {
        return this.get(`spots/${spotUuid}/driver-destinations`);
    }

    @cache(5 * 60000)
    getTransientDrivers(spotUuid: string, period: InsightsPeriod): Promise<TransientDrivers> {
        return this.get(`spots/${spotUuid}/transient-drivers?period=${period}`);
    }

    @cache(5 * 60000)
    getTenantUsage(spotUuid: string, period: TenantInsightsPeriod): Promise<TenantUsage[]> {
        return this.get(`spots/${spotUuid}/tenant-usage?period=${period}`);
    }

    @cache(5 * 60000)
    getTenantUsageMonthly(spotUuid: string, period: TenantInsightsPeriod): Promise<TenantUsageMonthly[]> {
        return this.get(`spots/${spotUuid}/monthly-tenant-usage?period=${period}`);
    }

    @cache(5 * 60000)
    getPropertyInsightsSummary(spotUuid: string): Promise<PropertyInsightsSummary> {
        return this.get(`spots/${spotUuid}/property-insights`);
    }
}

export default PropertyInsightsClient;
